<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import ApplicationScoreService from '@/core/conversations/application-score/application-score.service';

const props = defineProps<{
  jobId: number;
  plusOne?: boolean;
}>();

const applicationScoreService = new ApplicationScoreService();

const existingFeedbackCount = ref(0);
const animating = ref(false);
const totalRequired = ref(5);
const loading = ref(false);
const error = ref<string | null>(null);
const submitModifiedCount = computed(() => (props.plusOne ? 1 : 0));
const currentCount = computed(() => existingFeedbackCount.value + submitModifiedCount.value);

const progressPercentage = computed(() =>
  Math.min(100, (currentCount.value / totalRequired.value) * 100),
);

const remainingCount = computed(() =>
  Math.max(0, totalRequired.value - existingFeedbackCount.value),
);

const progressText = computed<{ headline: string; text: string }>(() => {
  const count = Math.min(
    Math.max(existingFeedbackCount.value + submitModifiedCount.value, 0),
    totalRequired.value,
  );
  switch (count) {
    case 0:
      return {
        headline: '⚡ Train your AI recruiter!',
        text: 'Detailed feedback improves candidate evaluation <b>every 5 submissions</b>',
      };
    case 1:
      return {
        headline: "🚀 First feedback submitted - you're powering up!",
        text: 'Amazing start! <b>4 more submissions</b> to supercharge your AI recruiter',
      };
    case 2:
      return {
        headline: "⚡ You're building momentum!",
        text: 'Keep it up! <b>3 more submissions</b> to enhanced candidate evaluation',
      };
    case 3:
      return {
        headline: '🚀 Halfway there!',
        text: "You're doing great! Just <b>2 more</b> to enhanced candidate evaluation",
      };
    case 4:
      return {
        headline: '🚀 Almost there!',
        text: 'Almost there! <b>1 more submission</b> to enhanced candidate evaluation',
      };
    case 5:
      return {
        headline: '🏆 Milestone complete! Your AI recruiter is learning',
        text: 'Your AI recruiter will apply improved evaluation criteria!',
      };
    default:
      return {
        headline: `⚡ Train your AI recruiter!'`,
        text: `Amazing start! <b>${remainingCount.value} more submissions</b> to supercharge your AI recruiter`,
      };
  }
});

const stepIndicator = computed(() => `${currentCount.value}/${totalRequired.value}`);

const fetchFeedbackCount = async () => {
  if (!props.jobId) return;

  loading.value = true;
  error.value = null;

  try {
    const result = await applicationScoreService.getFeedbackCount(props.jobId);
    existingFeedbackCount.value = result.currentCount;
    totalRequired.value = result.threshold;
  } catch (err) {
    error.value = 'Failed to load feedback count';
    // Error logged internally by service layer
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchFeedbackCount();
});

watch(
  () => props.jobId,
  () => {
    fetchFeedbackCount();
  },
);

watch(
  () => props.plusOne,
  () => {
    animating.value = props.plusOne;
    setTimeout(() => {
      animating.value = false;
    }, 500);
  },
  { immediate: true },
);
</script>

<template>
  <div class="mb-4">
    <div
      class="relative grid gap-2 rounded-lg border border-copilot-500 bg-copilot-0 p-4 transition-all duration-300 ease-out"
      :class="{ 'shadow-purple-500 scale-105 animate-pulse shadow-lg ': animating }"
      v-if="!loading && !error"
    >
      <div class="flex items-center justify-between">
        <div class="flex-1 text-sm font-bold text-highlight-700">
          {{ progressText.headline }}
        </div>

        <!-- Step indicator -->
        <div class="rounded px-2 py-1 text-sm font-bold text-highlight-700">
          {{ stepIndicator }}
        </div>
      </div>
      <!-- Progress bar -->
      <div>
        <div class="progress-bar-empty h-2 w-full rounded-full">
          <div
            class="progress-bar-filled h-2 rounded-full transition-all duration-300 ease-out"
            :style="{ width: `${progressPercentage}%` }"
          ></div>
        </div>
      </div>
      <div class="helper-text text-sm text-shade-820" v-html="progressText.text" />
    </div>
  </div>
</template>

<style scoped>
.progress-bar-filled {
  background: linear-gradient(270deg, #ae35f7 -15.75%, #5f30d1 71.06%);
}
.progress-bar-empty {
  background: linear-gradient(
    270deg,
    rgba(174, 53, 247, 0.1) -15.75%,
    rgba(95, 48, 209, 0.1) 71.06%
  );
}
.helper-text b {
  @apply font-bold;
}
</style>
